import { useState, useEffect } from 'react';
import { adminApi } from '../../api/adminApi';

interface Employee {
  id: number;
  full_name: string;
  office_number: string;
  photo_url?: string;
  description?: string;
}

export default function EmployeesAdmin() {
  const [fullName, setFullName] = useState('');
  const [office, setOffice] = useState('');
  const [desc, setDesc] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Стан для редагування
  const [editId, setEditId] = useState<number | null>(null);

  // --- Стан для кастомного модального вікна ---
  const [modal, setModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm?: () => void;
    isConfirm?: boolean;
  }>({ show: false, title: '', message: '' });

  const showAlert = (title: string, message: string) => {
    setModal({ show: true, title, message, isConfirm: false });
  };

  const showConfirm = (title: string, message: string, onConfirm: () => void) => {
    setModal({ show: true, title, message, onConfirm, isConfirm: true });
  };

  const closeModal = () => setModal(prev => ({ ...prev, show: false }));
  // -------------------------------------------

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;

  const loadEmployees = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/employees`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data);
      }
    } catch (err) {
      console.error('Помилка завантаження:', err);
    }
  };

  useEffect(() => { loadEmployees(); }, []);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    setIsUploading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      const data = await res.json();
      if (data.url) {
        setPhotoUrl(data.url);
      }
    } catch (err) {
      showAlert('Помилка', 'Не вдалося завантажити файл.');
    } finally {
      setIsUploading(false);
    }
  };

  const startEdit = (emp: Employee) => {
    setEditId(emp.id);
    setFullName(emp.full_name);
    setOffice(emp.office_number);
    setDesc(emp.description || '');
    setPhotoUrl(emp.photo_url || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditId(null);
    setFullName('');
    setOffice('');
    setDesc('');
    setPhotoUrl('');
  };

  const submit = async () => {
    if (!fullName || !office) return showAlert('Увага', 'Вкажіть ім’я та кабінет!');

    const employeeData = {
      full_name: fullName,
      office_number: office,
      photo_url: photoUrl,
      description: desc
    };

    try {
      if (editId) {
        await adminApi.updateEmployee(editId, employeeData);
        showAlert('Успіх', 'Дані лікаря оновлено!');
      } else {
        await adminApi.createEmployee(employeeData);
        showAlert('Успіх', 'Лікаря успішно додано!');
      }

      cancelEdit();
      loadEmployees();
    } catch {
      showAlert('Помилка', 'Сталася помилка при збереженні.');
    }
  };

  const remove = async (id: number) => {
    showConfirm('Видалення', 'Ви впевнені, що хочете видалити цього співробітника?', async () => {
      try {
        await adminApi.deleteEmployee(id);
        loadEmployees();
      } catch {
        showAlert('Помилка', 'Не вдалося видалити співробітника.');
      }
    });
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const currentEmployees = employees.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
  const totalPages = Math.ceil(employees.length / itemsPerPage);

  return (
    <div className="employees-admin-container">
      
      {/* КАСТОМНЕ МОДАЛЬНЕ ВІКНО */}
      {modal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>{modal.title}</h4>
            <p>{modal.message}</p>
            <div className="modal-actions">
              {modal.isConfirm && (
                <button onClick={closeModal} className="modal-btn-secondary">Скасувати</button>
              )}
              <button 
                onClick={() => { modal.onConfirm?.(); closeModal(); }} 
                className="modal-btn-primary"
              >
                ОК
              </button>
            </div>
          </div>
        </div>
      )}

      <h2 className="admin-title">👨‍⚕️ Керування співробітниками</h2>

      {/* ФОРМА */}
      <div className={`admin-form-card ${editId ? 'edit-mode' : ''}`}>
        <h3>{editId ? ' Редагувати дані лікаря' : ' Додати нову картку лікаря'}</h3>
        
        <div className="form-grid">
          <div className="inputs-column">
            <input 
              placeholder="ПІБ лікаря" 
              value={fullName} 
              onChange={e => setFullName(e.target.value)} 
              className="styled-input"
            />
            <input 
              placeholder="Номер кабінету" 
              value={office} 
              onChange={e => setOffice(e.target.value)} 
              className="styled-input"
            />
          </div>

          <div className={`file-upload-zone ${photoUrl ? 'has-file' : ''}`}>
            <label htmlFor="file-input" className="file-label">
              {isUploading ? (
                <span>⏳ Завантаження...</span>
              ) : photoUrl ? (
                <div className="preview-container">
                  <img src={photoUrl} alt="Preview" className="mini-preview" />
                  <span>Змінити фото</span>
                </div>
              ) : (
                <span>📷 Натисніть, щоб додати фото</span>
              )}
            </label>
            <input 
              id="file-input"
              type="file" 
              accept="image/*" 
              onChange={handleFileUpload} 
              hidden 
            />
          </div>
        </div>

        <textarea 
          placeholder="Опис кар'єри, освіти та досягнень..." 
          value={desc} 
          onChange={e => setDesc(e.target.value)} 
          className="styled-textarea"
        />

        <div className="form-buttons">
            <button onClick={submit} className="submit-btn">
                {editId ? 'Зберегти зміни' : 'Зберегти лікаря'}
            </button>
            {editId && (
                <button onClick={cancelEdit} className="cancel-btn">Скасувати</button>
            )}
        </div>
      </div>

      {/* СПИСОК */}
      <h3 className="list-title">📋 Список лікарів ({employees.length})</h3>
      <div className="employees-list">
        {currentEmployees.map(emp => (
          <div key={emp.id} className={`employee-card ${editId === emp.id ? 'being-edited' : ''}`}>
            <div className="emp-image">
              {emp.photo_url ? (
                <img src={emp.photo_url} alt={emp.full_name} />
              ) : (
                <div className="no-photo">Без фото</div>
              )}
            </div>

            <div className="emp-info">
              <div className="emp-header">
                <div className="emp-main-info">
                  <h4>{emp.full_name}</h4>
                  <p className="emp-meta">📍 Кабінет: <b>{emp.office_number}</b></p>
                </div>
                <div className="emp-btns">
                    <button onClick={() => startEdit(emp)} className="edit-btn">Редагувати</button>
                    <button onClick={() => remove(emp.id)} className="delete-btn">Видалити</button>
                </div>
              </div>
              <p className="emp-desc">{emp.description || 'Опис відсутній'}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ПАГІНАЦІЯ */}
      {totalPages > 1 && (
        <div className="pagination">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => { setCurrentPage(i + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className={currentPage === i + 1 ? 'active' : ''}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      <style>{`
        * { box-sizing: border-box; }

        .employees-admin-container {
          padding: 20px;
          max-width: 900px;
          margin: 0 auto;
          font-family: 'Montserrat', sans-serif;
          background-color: #f0f7f4;
          min-height: 100vh;
        }

        /* --- СТИЛІ МОДАЛЬНОГО ВІКНА --- */
        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          backdrop-filter: blur(2px);
        }

        .modal-content {
          background: white;
          padding: 30px;
          border-radius: 20px;
          max-width: 400px;
          width: 90%;
          text-align: center;
          box-shadow: 0 15px 35px rgba(0,0,0,0.2);
          animation: modalFadeIn 0.3s ease-out;
        }

        @keyframes modalFadeIn {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .modal-content h4 { margin: 0 0 15px; color: #2d5a50; font-size: 20px; }
        .modal-content p { color: #666; font-size: 16px; margin-bottom: 25px; line-height: 1.4; }

        .modal-actions { display: flex; gap: 10px; justify-content: center; }

        .modal-btn-primary {
          padding: 10px 25px;
          background: #1abc9c;
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 700;
          cursor: pointer;
        }

        .modal-btn-secondary {
          padding: 10px 25px;
          background: #edf2f0;
          color: #4a5568;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
        }
        /* ---------------------------- */

        .admin-title {
          text-align: center;
          color: #2d5a50;
          margin-bottom: 30px;
          font-weight: 700;
        }

        .admin-form-card {
          background: #fff;
          padding: 25px;
          border-radius: 16px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.05);
          margin-bottom: 40px;
          transition: border 0.3s ease;
        }

        .admin-form-card.edit-mode {
            border: 2px solid #1abc9c;
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 200px;
          gap: 20px;
          margin-bottom: 20px;
        }

        @media (max-width: 600px) {
          .form-grid { grid-template-columns: 1fr; }
        }

        .inputs-column {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .styled-input, .styled-textarea {
          width: 100%;
          padding: 14px;
          border: 2px solid #edf2f0;
          border-radius: 10px;
          font-size: 15px;
          outline: none;
          transition: border-color 0.3s;
        }

        .styled-input:focus, .styled-textarea:focus {
          border-color: #1abc9c;
        }

        .styled-textarea {
          height: 120px;
          resize: vertical;
          margin-bottom: 10px;
        }

        .file-upload-zone {
          border: 2px dashed #cbd5e0;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s;
          background: #f8faf9;
          min-height: 120px;
        }

        .file-upload-zone:hover {
          border-color: #1abc9c;
          background: #f0fdfa;
        }

        .file-upload-zone.has-file {
          border-style: solid;
          border-color: #1abc9c;
        }

        .file-label {
          cursor: pointer;
          font-size: 13px;
          color: #718096;
          padding: 10px;
          width: 100%;
        }

        .preview-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .mini-preview {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid #1abc9c;
        }

        .form-buttons {
            display: flex;
            gap: 10px;
        }

        .submit-btn {
          flex: 2;
          padding: 15px;
          background: #1abc9c;
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 700;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .submit-btn:hover {
          background: #16a085;
          transform: translateY(-2px);
        }

        .cancel-btn {
            flex: 1;
            padding: 15px;
            background: #cbd5e0;
            color: #4a5568;
            border: none;
            border-radius: 10px;
            font-weight: 700;
            cursor: pointer;
        }

        .list-title { color: #2d5a50; margin-bottom: 20px; }

        .employees-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .employee-card {
          display: flex;
          background: white;
          border-radius: 15px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0,0,0,0.02);
          border: 1px solid #eef2f1;
          transition: transform 0.2s;
        }

        .employee-card.being-edited {
            border: 2px solid #1abc9c;
            background: #f0fdfa;
        }

        .emp-image {
          width: 120px;
          min-width: 120px;
          background: #edf2f7;
        }

        .emp-image img { width: 100%; height: 100%; object-fit: cover; }

        .emp-info { flex: 1; padding: 15px; }

        .emp-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 10px;
        }

        .emp-btns {
            display: flex;
            gap: 8px;
        }

        .emp-main-info h4 { margin: 0; color: #1abc9c; font-size: 18px; }
        .emp-meta { margin: 4px 0; font-size: 14px; color: #718096; }

        .emp-desc {
          font-size: 14px;
          color: #4a5568;
          line-height: 1.5;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .edit-btn {
            padding: 8px 14px;
            background: #e6fffa;
            color: #2c7a7b;
            border: 1px solid #81e6d9;
            border-radius: 8px;
            font-weight: 600;
            font-size: 12px;
            cursor: pointer;
        }

        .delete-btn {
          padding: 8px 14px;
          background: #fff5f5;
          color: #e53e3e;
          border: 1px solid #feb2b2;
          border-radius: 8px;
          font-weight: 600;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .delete-btn:hover { background: #e53e3e; color: white; }

        .pagination {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin: 30px 0;
        }

        .pagination button {
          width: 40px;
          height: 40px;
          border: none;
          background: white;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 700;
          color: #2d5a50;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .pagination button.active {
          background: #1abc9c;
          color: white;
        }

        @media (max-width: 500px) {
          .employee-card { flex-direction: column; }
          .emp-image { width: 100%; height: 180px; }
        }
      `}</style>
    </div>
  );
}